/**
 * {country, digits} -> E.164 conversion utility (Issue #440).
 *
 * react-phone-number-input emits/consumes E.164 strings (e.g. "+19173028542").
 * The frontend stores phone answers as E.164 strings (the onChange handler
 * from NurturePhoneInput stores the E.164 value directly). The backend
 * ``_normalize_phone_answer`` in lead_capture.py accepts both E.164 strings
 * (legacy path) and {country, digits} objects (country selector path).
 *
 * ``objectToE164`` converts a stored {country, digits} object (if present
 * from an older answer shape) back to E.164 for react-phone-number-input.
 * Extracted from PublicForm.jsx and FormBuilder.jsx to avoid duplication
 * and make it unit-testable.
 */
import { getCountryCallingCode } from 'libphonenumber-js';

/**
 * Convert a {country, digits} object (from stored answers) to an E.164 string
 * for react-phone-number-input. Returns undefined if the value is empty/invalid.
 *
 * getCountryCallingCode() throws on an unknown ISO code — wrapped in try-catch
 * so a stale/corrupted stored answer doesn't crash the form render.
 */
export function objectToE164(value) {
    if (!value) return undefined;
    if (typeof value === 'string') return value || undefined;
    if (typeof value === 'object' && value.country && value.digits) {
        try {
            const dialCode = getCountryCallingCode(value.country);
            return `+${dialCode}${value.digits}`;
        } catch {
            return undefined;
        }
    }
    return undefined;
}
